import { memo, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import {
  Button,
  CheckBox,
  FormGroup,
  Input,
  ToggleButton,
  color,
  spacing
} from '@uniquegood/realworld-studio-design';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import { coreApi } from '@/api';
import { useRequest } from '@/hooks';
import { invalidFieldBoilerplate, toast } from '@/utils';
import Uploader from '@/components/Uploader';
import { decodeHtml, MISSION_API_VERSION_HEADER } from '@/pages/missions/utils';

type FormData = {
  name?: string;
  shortDescription?: string;
  coverImage?: [UserFileResponseModel];
  isVisibleDefault?: boolean;
  isLockedDefault?: boolean;
  forceChangeIsVisible?: boolean;
  forceChangeIsLocked?: boolean;
};

interface SidePanelMissionEditProps {
  missionId: number;
  mutate: KeyedMutator<Mission[]>;
  onDelete: () => void;
}

function transformToFormData(mission: Mission): FormData {
  return {
    name: mission.name,
    shortDescription: decodeHtml(mission.shortDescription),
    isVisibleDefault: mission.isVisibleDefault,
    isLockedDefault: mission.isLockedDefault,
    forceChangeIsVisible: false,
    forceChangeIsLocked: false,
    coverImage:
      mission.coverImageId && mission.coverImageUrl
        ? [{ id: mission.coverImageId, url: mission.coverImageUrl }]
        : undefined
  };
}

/** Extracted component to avoid calling useMemo inside Controller render prop */
const CoverImageField = memo(function CoverImageField({
  missionId,
  value,
  onChange
}: {
  missionId: number;
  value?: UserFileResponseModel[];
  onChange: (...event: unknown[]) => void;
}) {
  return (
    <FormGroup id="uploader" label="미션 카드 이미지">
      <Uploader
        key={missionId}
        id="uploader"
        hasSearch
        imageUseForType="mission-card-image"
        aiImageRatio="9:16"
        uploadedFiles={value}
        getUploadFiles={onChange}
        ratio="vertical"
        useFor="mission-cover"
        isAiProject
      />
    </FormGroup>
  );
});

export default function SidePanelMissionEdit({
  missionId,
  mutate,
  onDelete
}: SidePanelMissionEditProps) {
  const { appId, scenarioId } = useParams<AppParam>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: missions } = useRequest<Mission[]>(
    `/apps/${appId}/scenarios/${scenarioId}/missions`,
    {},
    MISSION_API_VERSION_HEADER
  );
  const mission = missions?.find((m) => m.id === missionId);

  const { control, handleSubmit, reset, formState, watch, setValue } = useForm<FormData>({
    defaultValues: mission ? transformToFormData(mission) : {}
  });

  useEffect(() => {
    if (mission) {
      reset(transformToFormData(mission));
    }
  }, [mission?.id]);

  const onSubmit = handleSubmit(async (data) => {
    setIsSubmitting(true);
    try {
      const { coverImage, ...rest } = data;
      await coreApi.put(`/apps/${appId}/missions/${missionId}`, {
        ...rest,
        coverImageId: coverImage?.[0]?.id,
        description: mission?.description || '<p></p>'
      }, MISSION_API_VERSION_HEADER);
      await mutate();
      toast({ message: '저장 완료!' });
    } finally {
      setIsSubmitting(false);
    }
  });

  const watchFormData = watch();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      css={FormLayout}
    >
      <Controller
        control={control}
        name="name"
        rules={{ required: true }}
        render={({ field: { value, onChange }, fieldState: { invalid } }) => (
          <FormGroup
            label="미션 이름"
            id="name"
            requiredIndicator
            errorText={invalidFieldBoilerplate(invalid)}
          >
            <Input id="name" value={value ?? ''} onChange={onChange} />
          </FormGroup>
        )}
      />

      <Controller
        control={control}
        name="shortDescription"
        render={({ field: { value, onChange } }) => (
          <FormGroup label="한 줄 설명" id="shortDescription">
            <Input id="shortDescription" value={value ?? ''} onChange={onChange} />
          </FormGroup>
        )}
      />

      <Controller
        control={control}
        name="coverImage"
        render={({ field: { value, onChange } }) => (
          <CoverImageField missionId={missionId} value={value} onChange={onChange} />
        )}
      />

      <div css={ToggleRow}>
        <FormGroup label="기본 표시 여부" cssStyle={ToggleGroupStyle}>
          <Controller
            control={control}
            name="isVisibleDefault"
            render={({ field: { value, onChange } }) => (
              <ToggleButton checked={value} onChange={onChange}>
                {value ? '표시' : '미표시'}
              </ToggleButton>
            )}
          />
          <CheckBox
            checked={watchFormData.forceChangeIsVisible}
            onChange={(e) => setValue('forceChangeIsVisible', e.target.checked, { shouldDirty: false })}
          >
            전체 강제 적용
          </CheckBox>
        </FormGroup>

        <FormGroup label="기본 잠금 여부" cssStyle={ToggleGroupStyle}>
          <Controller
            control={control}
            name="isLockedDefault"
            render={({ field: { value, onChange } }) => (
              <ToggleButton checked={value} onChange={onChange}>
                {value ? '잠금' : '미잠금'}
              </ToggleButton>
            )}
          />
          <CheckBox
            checked={watchFormData.forceChangeIsLocked}
            onChange={(e) => setValue('forceChangeIsLocked', e.target.checked, { shouldDirty: false })}
          >
            전체 강제 적용
          </CheckBox>
        </FormGroup>
      </div>

      <div css={ActionRow}>
        <Button
          type="plainDestructive"
          onClick={() => onDelete()}
        >
          미션 삭제
        </Button>
        <Button
          htmlType="submit"
          loading={isSubmitting}
          disabled={!formState.isDirty}
        >
          저장
        </Button>
      </div>
    </form>
  );
}

const FormLayout = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
`;

const ToggleRow = css`
  display: flex;
  gap: ${spacing.common.medium};
`;

const ToggleGroupStyle = css`
  flex: 1;

  > div {
    display: flex;
    align-items: center;
    gap: ${spacing.common.small};
  }
`;

const ActionRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${spacing.common.medium};
  border-top: 1px solid ${color.border_default_subdued};
`;
